import { useEffect, useState, type ReactNode } from 'react';
import { FONT_STACK, colors, layout } from '../theme.js';
import { channelColorOf } from '../channelColors.js';

/**
 * La piastrella di un film o di una serie, nella forma del prototipo v3.
 *
 * Locandina verticale 2:3, e sopra fino a tre contrassegni: **✓ VISTO** in alto a sinistra, il PIN
 * con la classificazione in alto a destra, e la barra ambra dell'avanzamento in basso. Sotto, titolo
 * su una riga con i puntini, sottotitolo grigio, e — quando c'è — la nota ambra: il motivo del
 * consiglio, o da quanto è in catalogo.
 *
 * ## Perché l'avanzamento è una barra e non una percentuale
 *
 * Perché a tre metri «47%» non si legge e non serve: la domanda è «quanto manca», e a quella una barra
 * risponde da sola. È la stessa ragione per cui il ✓ è un distintivo e non una riga di testo.
 *
 * ## Senza locandina ci va **il titolo**, non un'iniziale
 *
 * Su un catalogo importato le locandine mancano a macchia, e non è un caso raro: sul catalogo di
 * sviluppo, il 28-09-2026, **3524 film su 10633** portano un indirizzo TMDB troncato — `…/w600_and_h900_bestv2`
 * senza il nome del file — e altri 160 non ne hanno affatto. Un `<img>` con un indirizzo morto non
 * resta vuoto: mostra l'icona dell'immagine rotta del browser, che su un televisore a tre metri è un
 * rettangolo grigio con una crocetta.
 *
 * Il ripiego era `initialsOf`: una lettera sola, grande, al posto della locandina. Su un terzo del
 * catalogo la vetrina diventava una fila di lettere — e il titolo, che pure è scritto sotto in 22 px,
 * non è quel che l'occhio cerca guardando una griglia di locandine. È la stessa correzione già fatta
 * sui canali («cosa sono quelle lettere inutili nella card?»): dentro il riquadro ci va il **nome**,
 * su un fondo che è del titolo.
 */

export interface PosterTileProps {
  readonly title: string;
  /** La riga grigia sotto il titolo: genere, anno, numero di stagioni. La compone chi chiama. */
  readonly subtitle: string;
  readonly posterUrl: string | null;
  /** 0–100, oppure `null` quando non c'è niente da mostrare. */
  readonly percent: number | null;
  readonly watched: boolean;
  /** La classificazione quando il titolo chiede il PIN, `null` altrimenti. */
  readonly lockedRating: string | null;
  /** La nota ambra: il perché del consiglio, o «aggiunto tre giorni fa». */
  readonly note?: string | null;
  readonly focused: boolean;
  readonly onSelect: () => void;
  /** Le piastrelle degli episodi sono orizzontali 16:9 e più larghe: vedi la scheda di una serie. */
  readonly variant?: 'poster' | 'episode';
}

export function PosterTile({
  title,
  subtitle,
  posterUrl,
  percent,
  watched,
  lockedRating,
  note = null,
  focused,
  onSelect,
  variant = 'poster',
}: PosterTileProps): ReactNode {
  const [failed, setFailed] = useState(false);
  /*
    Se l'immagine che è arrivata ha la forma del riquadro.

    Serve solo alla piastrella dell'episodio, che è 16:9. Un gateway può mandare lì una locandina
    2:3 — la nostra lo faceva, e c'è chi lo fa ancora — e `cover` la ritaglierebbe a fetta
    orizzontale presa in mezzo: tutti gli episodi della serie con la stessa striscia, che si legge
    come un'immagine sbagliata e non come una mancante. Non si può sapere prima di averla caricata,
    quindi si guarda quando arriva.
  */
  const [portrait, setPortrait] = useState(false);

  // Come in `ChannelLogo`: le fasce si riciclano scorrendo, e un indirizzo morto non deve marchiare
  // come rotta la piastrella che riusa quel nodo dopo.
  useEffect(() => {
    setFailed(false);
    setPortrait(false);
  }, [posterUrl]);

  const episode = variant === 'episode';
  // Il colore del ripiego: dal titolo, così è sempre lo stesso per lo stesso titolo.
  const color = channelColorOf(title);
  const width = episode ? layout.episodeTileWidth : layout.posterTileWidth;
  const height = episode ? layout.episodeTileHeight : layout.posterTileHeight;

  return (
    <button
      type="button"
      data-f="1"
      data-poster="1"
      data-focused={focused ? '1' : '0'}
      onClick={onSelect}
      style={{
        flex: 'none',
        width,
        border: 0,
        margin: 0,
        padding: 0,
        textAlign: 'left',
        background: 'transparent',
        color: colors.text,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        borderRadius: 14,
        cursor: 'pointer',
        /*
          `undefined` e non `'none'` quando non è a fuoco.

          Sono due modi diversi di dire dove sta il fuoco, e questa piastrella serve a entrambi. Le
          schermate a cursore tengono un **indice** e non spostano il fuoco del DOM (vedi
          `scrollFocusedIntoView`): lì il contorno lo decide `focused`. La ricerca invece usa la
          navigazione spaziale, che sposta il fuoco vero, e il contorno arriva dalla regola globale
          `*:focus` in `index.html`.

          Uno stile in linea batte il foglio di stile: scrivendo `'none'`, in ricerca la locandina a
          fuoco non avrebbe **nessun** contorno — il fuoco sarebbe invisibile proprio sulla griglia
          dove serve di più. Con `undefined` la proprietà non si scrive affatto e la regola globale
          torna a valere; nelle schermate a cursore non cambia niente, perché lì nessun elemento ha
          davvero il fuoco.
        */
        outline: focused ? `4px solid ${colors.accent}` : undefined,
        outlineOffset: 4,
        transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
        transition: 'var(--tr, transform .16s ease)',
      }}
    >
      <div
        style={{
          position: 'relative',
          height,
          borderRadius: 14,
          overflow: 'hidden',
          // Le due righe diagonali del prototipo: dicono «qui va un'immagine» senza sembrare un guasto.
          background: `repeating-linear-gradient(135deg,${colors.surfaceRaised} 0 14px,${colors.surfaceProgram} 14px 28px)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {posterUrl !== null && !failed ? (
          <img
            src={posterUrl}
            alt=""
            onError={() => setFailed(true)}
            onLoad={(event) => {
              const image = event.currentTarget;
              if (image.naturalHeight > image.naturalWidth) setPortrait(true);
            }}
            /*
              `cover` è il caso normale: una locandina *è* 2:3, e lo spazio intorno su una griglia di
              locandine si legge come una piastrella difettosa in mezzo a quelle giuste.

              L'eccezione è una verticale dentro il riquadro orizzontale dell'episodio: lì `cover`
              taglia la testa a chiunque, e vedere l'immagine intera con due bande ai lati è meno
              peggio che vederne una fetta.
            */
            style={{
              width: '100%',
              height: '100%',
              objectFit: episode && portrait ? 'contain' : 'cover',
            }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span
            data-poster-fallback="1"
            style={{
              /*
                Il titolo per esteso, su un fondo che è **suo**.

                Il colore si deriva dal titolo e non cambia fra due disegni: un colore casuale
                insegna a non fidarsene, e su una griglia il colore è quel che permette di ritrovare
                una piastrella senza rileggerla.
              */
              position: 'absolute',
              inset: 0,
              background: color.background,
              color: color.foreground,
              display: '-webkit-box',
              WebkitLineClamp: episode ? 3 : 5,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              alignItems: 'center',
              justifyContent: 'center',
              font: `800 ${episode ? 24 : 28}px/1.2 ${FONT_STACK}`,
              letterSpacing: '-.01em',
              padding: '0 18px',
              textAlign: 'center',
              boxSizing: 'border-box',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </span>
        )}

        {watched && (
          <span
            data-watched-badge="1"
            style={{
              position: 'absolute',
              left: 12,
              top: 12,
              padding: '6px 10px',
              borderRadius: 8,
              background: colors.success,
              color: '#06140D',
              font: `800 16px ${FONT_STACK}`,
              letterSpacing: '.04em',
            }}
          >
            ✓
          </span>
        )}

        {lockedRating !== null && (
          <span
            data-locked-badge="1"
            style={{
              position: 'absolute',
              right: 12,
              top: 12,
              padding: '5px 9px',
              borderRadius: 8,
              background: colors.background,
              color: colors.accent,
              border: `2px solid ${colors.accent}`,
              font: `800 15px ${FONT_STACK}`,
            }}
          >
            PIN · {lockedRating}
          </span>
        )}

        {percent !== null && percent > 0 && (
          <div
            data-progress-bar="1"
            style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 6, background: colors.border }}
          >
            <div style={{ height: '100%', width: `${Math.min(100, percent)}%`, background: colors.accent }} />
          </div>
        )}
      </div>

      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px 4px', minWidth: 0 }}>
        <span
          style={{
            font: `600 ${episode ? 24 : 22}px ${FONT_STACK}`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        <span
          style={{
            font: `400 18px ${FONT_STACK}`,
            color: colors.textDim,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle}
        </span>
        {note !== null && note !== '' && (
          <span data-poster-note="1" style={{ font: `500 17px/1.3 ${FONT_STACK}`, color: colors.accent }}>
            {note}
          </span>
        )}
      </span>
    </button>
  );
}
